import { Scene, Vector3 } from 'three';

import { CameraHelper } from './camera-helper.class';
import {
	CAMERA_HELPER_DEFAULTS,
	CameraHelperManagerConfig,
	RenderQuality,
	VisualizableCamera
} from './camera-helper.types';

/**
 * Manages visualization helpers for cameras
 *
 * Unlike LightHelperManager, cameras are not auto-discovered from the scene
 * since cameras are typically managed explicitly. Use registerCamera() to
 * add cameras to be visualized.
 *
 * @example
 * ```typescript
 * const manager = new CameraHelperManager({ scene, visible: true });
 * manager.registerCamera(mainCamera, pivotPosition);
 *
 * // In render loop
 * manager.update(pivotPosition);
 *
 * // Toggle visibility
 * manager.setVisible(false);
 *
 * // Clean up
 * manager.dispose();
 * ```
 */
export class CameraHelperManager {
	/** Scene reference */
	#scene: Scene;

	/** Map of camera to helper */
	#helpers: Map<VisualizableCamera, CameraHelper> = new Map();

	/** Configuration */
	#coneRadius: number;
	#coneHeight: number;
	#coneColor: number;
	#lineColor: number;
	#visible: boolean;
	#quality: RenderQuality;

	constructor (config: CameraHelperManagerConfig) {
		this.#scene = config.scene;
		this.#coneRadius = config.coneRadius ?? CAMERA_HELPER_DEFAULTS.CONE_RADIUS;
		this.#coneHeight = config.coneHeight ?? CAMERA_HELPER_DEFAULTS.CONE_HEIGHT;
		this.#coneColor = config.coneColor ?? CAMERA_HELPER_DEFAULTS.CONE_COLOR;
		this.#lineColor = config.lineColor ?? CAMERA_HELPER_DEFAULTS.LINE_COLOR;
		this.#visible = config.visible ?? false;
		this.#quality = config.quality ?? CAMERA_HELPER_DEFAULTS.QUALITY;
	}

	/** Get visibility state */
	get visible (): boolean {
		return this.#visible;
	}

	/** Get number of managed helpers */
	get count (): number {
		return this.#helpers.size;
	}

	/**
	 * Register a camera to be visualized
	 *
	 * @param camera Camera to visualize
	 * @param targetPosition Optional initial target position
	 */
	registerCamera (camera: VisualizableCamera, targetPosition?: Vector3): void {
		if (this.#helpers.has(camera)) {
			return;
		}

		const helper = new CameraHelper(camera, {
			scene: this.#scene,
			coneRadius: this.#coneRadius,
			coneHeight: this.#coneHeight,
			coneColor: this.#coneColor,
			lineColor: this.#lineColor,
			visible: this.#visible,
			targetPosition,
			quality: this.#quality
		});

		this.#helpers.set(camera, helper);
	}

	/**
	 * Unregister a camera
	 */
	unregisterCamera (camera: VisualizableCamera): void {
		const helper = this.#helpers.get(camera);
		if (helper) {
			helper.dispose();
			this.#helpers.delete(camera);
		}
	}

	/**
	 * Set visibility for all helpers
	 */
	setVisible (visible: boolean): void {
		this.#visible = visible;
		for (const helper of this.#helpers.values()) {
			helper.visible = visible;
		}
	}

	/**
	 * Set cone dimensions for all helpers
	 */
	setConeDimensions (radius: number, height: number): void {
		this.#coneRadius = radius;
		this.#coneHeight = height;
		for (const helper of this.#helpers.values()) {
			helper.setConeDimensions(radius, height);
		}
	}

	/**
	 * Get render quality (0=wireframe, 1-10=quality level)
	 */
	get quality (): RenderQuality {
		return this.#quality;
	}

	/**
	 * Set render quality for all helpers (0=wireframe, 1-10=quality level)
	 */
	set quality (value: RenderQuality) {
		if (this.#quality === value) return;
		this.#quality = value;
		for (const helper of this.#helpers.values()) {
			helper.quality = value;
		}
	}

	/**
	 * Set render quality for all helpers (0=wireframe, 1-10=quality level)
	 */
	setQuality (value: RenderQuality): void {
		this.quality = value;
	}

	/**
	 * Get helper for a specific camera
	 */
	getHelper (camera: VisualizableCamera): CameraHelper | undefined {
		return this.#helpers.get(camera);
	}

	/**
	 * Update all helpers
	 *
	 * @param targetPosition Target position for all cameras (e.g., pivot point)
	 */
	update (targetPosition?: Vector3): void {
		for (const helper of this.#helpers.values()) {
			helper.update(targetPosition);
		}
	}

	/**
	 * Dispose of all helpers
	 */
	dispose (): void {
		for (const helper of this.#helpers.values()) {
			helper.dispose();
		}
		this.#helpers.clear();
	}
}

